import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import {
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerModule,
  NgbDateStruct,
} from "@ng-bootstrap/ng-bootstrap";

import { AdaaHelper, InputDateAdapter, InputDateFormatterAdapter } from "../../../../core/utils";
import { ParameterCatalog } from "../../../models";

@Component({
  selector: "adaa-form-input-date",
  imports: [ReactiveFormsModule, NgbDatepickerModule, FontAwesomeModule],
  templateUrl: "./form-input-date.html",
  styleUrl: "./form-input-date.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputDateComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputDateComponent),
    },
    {
      provide: NgbDateAdapter,
      useClass: InputDateAdapter,
    },
    {
      provide: NgbDateParserFormatter,
      useClass: InputDateFormatterAdapter,
    },
  ],
})
export class FormInputDateComponent implements ControlValueAccessor {
  readonly inputControl = new FormControl();
  readonly AdaaHelper = AdaaHelper;

  private readonly _iconLibrary = inject(FaIconLibrary);
  private readonly _dateAdapter: NgbDateAdapter<number> = inject(NgbDateAdapter);

  private readonly _requiredFromValidator = signal<boolean>(false);
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: number | null) => void;
  private _disabledByInput = false;
  private readonly _disabledByFormControl = signal<boolean>(false);

  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly placeholder = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly maxDate = input<NgbDateStruct | null>();
  readonly minDate = input<NgbDateStruct | null>();
  readonly setDefaultValue = input<unknown>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());
  readonly effectivelyDisabled = computed(() => this.isDisabled() || this._disabledByFormControl());

  constructor() {
    this._iconLibrary.addIcons(faCalendar);

    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value) {
          this.writeValue(value as number);
        } else {
          this.inputControl.reset();
        }
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => this._requiredFromValidator.set(field.mandatory === "Y"));
        }
      }
    });
  }

  public writeValue(obj: number): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    this.inputControl.setValue(obj);
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this._disabledByFormControl.set(isDisabled);
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return (
      isRequired && {
        required: true,
      }
    );
  }

  public valueChanged(event: NgbDate): void {
    const date = this._dateAdapter.toModel(event);
    if (this._onChange) {
      this._onChange(date);
    }
    this.inputChanges.emit(date);
  }
}
